import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../poker-ui/utils/cn';
import './ArmenianAuctionRevealFx.css';
import {
  isStrictlyStrongerBid,
  TRUMP_NO_TRUMP,
  TRUMP_ALL_TRUMP,
} from '../../shared/game/belot/armenian/auction';
/** @typedef {0|1|2|3|typeof TRUMP_NO_TRUMP|typeof TRUMP_ALL_TRUMP} TrumpChoice */

/** Порядок как в портретном референсе: ♠ ♥ ♣ ♦, затем без козыря и AT («А»). */
const SUIT_ROW_SUITS = [
  { trump: 3, label: '♠', name: 'Пики' },
  { trump: 2, label: '♥', name: 'Черви' },
  { trump: 0, label: '♣', name: 'Трефы' },
  { trump: 1, label: '♦', name: 'Бубны' },
];
const SUIT_ROW_NT = [
  { trump: TRUMP_NO_TRUMP, label: 'БК', name: 'Без козыря' },
  { trump: TRUMP_ALL_TRUMP, label: 'А', name: 'Все козыри (AT)' },
];
const SUIT_GRID = [...SUIT_ROW_SUITS, ...SUIT_ROW_NT];

const TENS_MIN = 10;
const TENS_MAX_NORMAL = 24;
const TENS_KAPOT = 25;

/** Согласовано с `SekaActionBar`: градиент, blur, focus/active. */
const actionBtnBase = cn(
  'relative flex min-h-[2.3rem] items-center justify-center rounded-xl border px-1.5 text-center',
  'text-[10px] font-semibold uppercase leading-snug tracking-tight transition-all duration-150 sm:min-h-[2.5rem] sm:px-2 sm:text-[11px]',
  'shadow-sm backdrop-blur-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  'active:scale-[0.96] active:brightness-110 disabled:pointer-events-none disabled:opacity-45 disabled:saturate-50',
);

const btnShine = (
  <span
    className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-xl bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-60"
    aria-hidden
  />
);

const panelShell = cn(
  'border border-border/70 bg-card/95 text-foreground shadow-[0_12px_40px_rgba(0,0,0,0.55)] backdrop-blur-md',
);

const contraPrimaryStyle = cn(
  'border-red-500/55 bg-gradient-to-b from-red-700/75 to-red-950/65 text-red-50 shadow-[0_6px_22px_rgba(239,68,68,0.28)]',
  'hover:border-red-400/65 hover:from-red-600/80 hover:to-red-950/70 hover:shadow-[0_8px_28px_rgba(239,68,68,0.38)]',
  'focus-visible:ring-red-400/45',
);

const recontraStyle = cn(
  'border-rose-400/45 bg-gradient-to-b from-rose-900/65 to-red-950/55 text-rose-50',
  'hover:border-rose-300/55 hover:shadow-[0_4px_18px_rgba(251,113,133,0.22)]',
  'focus-visible:ring-rose-400/35',
);

const orderDisabledStyle = cn(
  'cursor-not-allowed border-border/50 bg-muted/30 text-muted-foreground opacity-80',
  'focus-visible:ring-muted-foreground/20',
);

const cyanControlStyle = cn(
  'border-cyan-500/30 bg-gradient-to-b from-cyan-950/50 to-slate-950/40 text-cyan-100/95',
  'hover:border-cyan-400/45 hover:from-cyan-900/55 hover:to-slate-900/45 hover:shadow-[0_6px_22px_rgba(34,211,238,0.18)]',
  'focus-visible:ring-cyan-400/40',
);

const suitActiveStyle = cn(
  'border-cyan-400/50 bg-gradient-to-b from-cyan-900/55 to-slate-950/45 text-cyan-50',
  'shadow-[0_6px_20px_rgba(34,211,238,0.22)] ring-1 ring-cyan-400/25',
  'hover:from-cyan-800/60 hover:to-slate-900/50',
  'focus-visible:ring-cyan-400/40',
);

const kapotIdleStyle = cn(
  'border-amber-400/40 bg-gradient-to-b from-amber-950/45 to-slate-950/40 text-amber-100/95',
  'hover:border-amber-300/55 hover:from-amber-900/55 hover:to-slate-900/45 hover:shadow-[0_6px_22px_rgba(251,191,36,0.2)]',
  'focus-visible:ring-amber-400/40',
);

const kapotActiveStyle = cn(
  'border-amber-300/55 bg-gradient-to-b from-amber-900/60 via-amber-950/50 to-slate-950/35 text-amber-50',
  'shadow-[0_6px_22px_rgba(251,191,36,0.22)] ring-1 ring-amber-300/30',
  'focus-visible:ring-amber-400/40',
);

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {{ tens: number, trump: unknown, kapot?: boolean }|null|undefined} props.highestBid
 * @param {() => void} props.onPass
 * @param {(bid: { tens: number, trump: number|typeof TRUMP_NO_TRUMP, kapot: boolean }) => void} props.onBid
 * @param {number} [props.contraMultiplier]
 * @param {boolean} [props.canContra]
 * @param {boolean} [props.canRecontra]
 * @param {boolean} [props.auctionMultIsMax]
 * @param {() => void} [props.onContra]
 * @param {() => void} [props.onRecontra]
 * @param {(sound: string, volume?: number) => void} [props.playUi]
 * @param {string} [props.turnHeadline]
 * @param {boolean} [props.disabledAll]
 * @param {string} [props.passLabel]
 * @param {string} [props.orderLabel]
 * @param {'armenian'|'moldovan'} [props.variant]
 * @param {'modal'|'inline'} [props.presentation] — `modal`: fixed overlay (локальный лобби); `inline`: в потоке (онлайн-стол без sticky+fixed конфликта)
 */
export function ArmenianAuctionPanel({
  open,
  highestBid,
  onPass,
  onBid,
  contraMultiplier = 1,
  canContra = false,
  canRecontra = false,
  auctionMultIsMax = false,
  onContra,
  onRecontra,
  playUi,
  turnHeadline = 'Ваш ход в торгах',
  disabledAll = false,
  passLabel = 'Пас',
  orderLabel = 'Заказать',
  variant = 'armenian',
  presentation = 'modal',
}) {
  const isMoldovan = variant === 'moldovan';
  const tensMin = TENS_MIN;
  const tensMaxNormal = isMoldovan ? 25 : TENS_MAX_NORMAL;
  const [levelMode, setLevelMode] = useState(/** @type {'idle'|'normal'|'kapot'} */ ('idle'));
  const [normalTens, setNormalTens] = useState(/** @type {number|null} */ (null));
  const [trump, setTrump] = useState(/** @type {TrumpChoice|null} */ (null));
  const [hasManualBidTouch, setHasManualBidTouch] = useState(false);
  const [swipeAnchorX, setSwipeAnchorX] = useState(/** @type {number|null} */ (null));

  useEffect(() => {
    if (!open) {
      setLevelMode('idle');
      setNormalTens(null);
      setTrump(null);
      setHasManualBidTouch(false);
      setSwipeAnchorX(null);
    }
  }, [open]);

  const selectedTens = useMemo(() => {
    if (levelMode === 'kapot' && !isMoldovan) return TENS_KAPOT;
    if (levelMode === 'normal' && normalTens != null) return normalTens;
    return null;
  }, [isMoldovan, levelMode, normalTens]);

  const selectedKapot = !isMoldovan && levelMode === 'kapot';

  const candidateBid = useMemo(() => {
    if (selectedTens == null || trump == null) return null;
    return {
      tens: selectedTens,
      trump,
      kapot: selectedKapot,
    };
  }, [selectedTens, trump, selectedKapot]);

  /** И онлайн (armenian / moldovan / open_duell), и локальный стол ходят через `armenian/auction` — один порядок силы заявок. */
  const canOrder = useMemo(() => {
    if (!candidateBid) return false;
    return isStrictlyStrongerBid(candidateBid, highestBid ?? null);
  }, [candidateBid, highestBid]);

  /** На том же уровне десятков можно перебить сильнее мастью/режимом (как `compareBids` в движке). */
  const hasSameLevelSteal = useMemo(() => {
    if (!isMoldovan || !highestBid) return false;
    return SUIT_GRID.some((s) => {
      const probe = { tens: Number(highestBid.tens), trump: s.trump, kapot: false };
      return isStrictlyStrongerBid(probe, highestBid);
    });
  }, [highestBid, isMoldovan]);
  const minAllowedTens = useMemo(() => {
    if (!highestBid) return tensMin;
    const top = Number(highestBid.tens);
    if (!Number.isFinite(top)) return tensMin;
    if (isMoldovan && hasSameLevelSteal) {
      return Math.max(tensMin, Math.min(tensMaxNormal, top));
    }
    return Math.max(tensMin, Math.min(tensMaxNormal, top + 1));
  }, [hasSameLevelSteal, highestBid, isMoldovan, tensMaxNormal, tensMin]);

  useEffect(() => {
    if (hasManualBidTouch) return;
    setLevelMode('normal');
    setNormalTens(minAllowedTens);
  }, [hasManualBidTouch, minAllowedTens]);

  const bumpTens = useCallback(
    (delta) => {
      playUi?.('belot-auction-click', 0.2);
      if (levelMode === 'kapot' && !isMoldovan) {
        setLevelMode('normal');
        setNormalTens(delta < 0 ? tensMaxNormal : tensMin);
        setHasManualBidTouch(true);
        return;
      }
      setLevelMode('normal');
      setNormalTens((prev) => {
        const base = prev ?? minAllowedTens;
        return Math.min(tensMaxNormal, Math.max(minAllowedTens, base + delta));
      });
      setHasManualBidTouch(true);
    },
    [isMoldovan, levelMode, minAllowedTens, playUi, tensMaxNormal, tensMin],
  );

  const handleKapotToggle = useCallback(() => {
    playUi?.('belot-auction-click', 0.28);
    if (isMoldovan) {
      return;
    }
    if (levelMode === 'kapot') {
      setLevelMode('normal');
      setNormalTens((t) => t ?? tensMin);
      setHasManualBidTouch(true);
    } else {
      setLevelMode('kapot');
      setNormalTens(null);
      setHasManualBidTouch(true);
    }
  }, [isMoldovan, levelMode, playUi, tensMin]);

  const handleOrder = useCallback(() => {
    if (!candidateBid || !canOrder) return;
    onBid(candidateBid);
  }, [candidateBid, canOrder, onBid]);

  const cycleNtTrump = useCallback(() => {
    playUi?.('belot-auction-click', 0.22);
    setTrump((prev) => {
      if (prev === TRUMP_NO_TRUMP) return TRUMP_ALL_TRUMP;
      if (prev === TRUMP_ALL_TRUMP) return TRUMP_NO_TRUMP;
      return TRUMP_NO_TRUMP;
    });
    setHasManualBidTouch(true);
  }, [playUi]);

  const showContraBlock = Boolean(highestBid && (contraMultiplier > 1 || canContra || canRecontra));

  const sandSideStyle = cn(
    'border-amber-200/40 bg-gradient-to-b from-amber-900/55 to-[#2a2218]/90 text-amber-50',
    'hover:border-amber-300/50 hover:from-amber-800/65 hover:to-[#342a1c]/92',
    'focus-visible:ring-amber-400/35',
  );

  if (!open) return null;

  const isInline = presentation === 'inline';

  /** В потоке над рукой: компактнее по вертикали на узком экране. */
  const panelInnerMaxHClass = isInline ? 'max-h-[min(35dvh,228px)] sm:max-h-[min(34vh,260px)]' : 'max-h-[min(46vh,400px)]';
  const row1MinH = isInline ? 'min-h-[2.25rem]' : 'min-h-[2.9rem]';
  const sideBtnMin = isInline ? 'min-w-[2.15rem]' : 'min-w-[2.5rem]';
  const suitBtnRound = isInline ? 'h-10 w-10 text-[18px]' : 'h-11 w-11 text-xl';
  const suitWrapGap = isInline ? 'gap-1.5' : 'gap-2 sm:gap-2.5';
  const row3Min = isInline ? 'min-h-0' : 'min-h-[3rem]';

  const panelInner = (
    <div
      className={cn(
        'flex flex-col overflow-y-auto overscroll-contain',
        isInline
          ? 'gap-1 px-1.5 pb-1 pt-1 sm:gap-1.5 sm:px-2.5 sm:pb-1.5 sm:pt-1.5'
          : 'gap-2 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:gap-2 sm:px-3.5 sm:pb-2.5 sm:pt-2.5',
        panelInnerMaxHClass,
      )}
    >
      {/* Ряд 1: степпер CP + Капо */}
      <section aria-label="Очки и капот">
        <div className={cn('flex items-stretch', isInline ? 'gap-1' : 'gap-2')}>
          <div
            className={cn(
              'flex min-w-0 flex-1 items-stretch gap-1 rounded-xl border border-border/45 bg-muted/20 p-0.5 sm:p-1',
              row1MinH,
            )}
          >
            <button
              type="button"
              aria-label="Меньше CP"
              disabled={(!isMoldovan && levelMode === 'kapot') || disabledAll}
              onClick={() => bumpTens(-1)}
              className={cn(
                actionBtnBase,
                'min-h-0 shrink-0 rounded-lg px-0 py-0',
                sideBtnMin,
                !isMoldovan && levelMode === 'kapot' ? orderDisabledStyle : cyanControlStyle,
              )}
            >
              {isMoldovan || levelMode !== 'kapot' ? btnShine : null}
              <ChevronLeft className={isInline ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={2.5} />
            </button>
            <div
              className="flex min-w-0 flex-1 flex-col items-center justify-center rounded-lg border border-border/25 bg-background/50 px-1"
              onPointerDown={(e) => setSwipeAnchorX(e.clientX)}
              onPointerUp={(e) => {
                if (!isMoldovan || swipeAnchorX == null) return;
                const dx = e.clientX - swipeAnchorX;
                setSwipeAnchorX(null);
                if (Math.abs(dx) < 20) return;
                bumpTens(dx > 0 ? 1 : -1);
              }}
              onPointerCancel={() => setSwipeAnchorX(null)}
              title={isMoldovan ? 'Свайп для изменения CP' : undefined}
            >
              {!isMoldovan && levelMode === 'kapot' ? (
                <>
                  <span
                    key="kapot-cp"
                    className={cn(
                      'belot-auction-cp-drum font-mono font-black tabular-nums leading-none text-amber-100',
                      isInline ? 'text-xl' : 'text-2xl',
                    )}
                  >
                    25
                  </span>
                  <span className="mt-0.5 text-[9px] font-bold uppercase leading-none tracking-wide text-amber-200/90">
                    CP · капот
                  </span>
                </>
              ) : (
                <>
                  <span
                    key={
                      normalTens != null
                        ? `cp-${normalTens}`
                        : minAllowedTens != null
                          ? `cp-min-${minAllowedTens}`
                          : 'cp-empty'
                    }
                    className={cn(
                      'belot-auction-cp-drum font-mono font-black tabular-nums leading-none',
                      isInline ? 'text-xl' : 'text-2xl',
                      normalTens != null
                        ? 'text-white'
                        : isMoldovan
                          ? 'text-slate-400'
                          : 'text-muted-foreground',
                    )}
                  >
                    {normalTens != null
                      ? normalTens
                      : minAllowedTens != null
                        ? minAllowedTens
                        : '—'}
                  </span>
                  <span className="mt-0.5 font-sans text-[9px] font-semibold uppercase leading-none tracking-wide text-muted-foreground">
                    CP
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              aria-label="Больше CP"
              disabled={(!isMoldovan && levelMode === 'kapot') || disabledAll}
              onClick={() => bumpTens(1)}
              className={cn(
                actionBtnBase,
                'min-h-0 shrink-0 rounded-lg px-0 py-0',
                sideBtnMin,
                !isMoldovan && levelMode === 'kapot' ? orderDisabledStyle : cyanControlStyle,
              )}
            >
              {isMoldovan || levelMode !== 'kapot' ? btnShine : null}
              <ChevronRight className={isInline ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={2.5} />
            </button>
          </div>
          {!isMoldovan ? (
            <button
              type="button"
              disabled={disabledAll}
              onClick={handleKapotToggle}
              title="25 CP · капот (8 взяток)"
              className={cn(
                actionBtnBase,
                row1MinH,
                'w-[28%] max-w-[6.2rem] shrink-0 flex-col gap-0 py-0.5 text-[9px] font-black leading-tight sm:py-1 sm:text-[10px]',
                levelMode === 'kapot' ? kapotActiveStyle : kapotIdleStyle,
              )}
            >
              {btnShine}
              <span>Капо</span>
            </button>
          ) : (
            <button
              type="button"
              disabled={disabledAll}
              onClick={() => {
                playUi?.('belot-auction-click', 0.22);
                setLevelMode('normal');
                setNormalTens(tensMaxNormal);
                setHasManualBidTouch(true);
              }}
              title="25 CP"
              className={cn(
                actionBtnBase,
                row1MinH,
                'w-[28%] max-w-[6.2rem] shrink-0 flex-col py-0.5 text-[9px] font-bold sm:py-1 sm:text-[10px]',
                cyanControlStyle,
              )}
            >
              {btnShine}
              25 CP
            </button>
          )}
        </div>
      </section>

      {/* Ряд 2: пять кругов — четыре масти + А (БК/AT) */}
      <section aria-label="Масть" className="min-w-0">
        <span className="sr-only">{isMoldovan ? 'Режим и масть' : 'Масть'}</span>
        <div className={cn('flex flex-wrap items-center justify-center', suitWrapGap)}>
          {SUIT_ROW_SUITS.map((s) => {
            const active = trump === s.trump;
            const red = s.trump === 1 || s.trump === 2;
            const tieBreakAllowed =
              isMoldovan &&
              selectedTens != null &&
              highestBid &&
              selectedTens === Number(highestBid.tens) &&
              isStrictlyStrongerBid({ tens: selectedTens, trump: s.trump, kapot: false }, highestBid);
            const tieBreakBlocked =
              isMoldovan &&
              selectedTens != null &&
              highestBid &&
              selectedTens === Number(highestBid.tens) &&
              !tieBreakAllowed;
            return (
              <button
                key={String(s.trump)}
                type="button"
                title={s.name}
                disabled={disabledAll}
                onClick={() => {
                  playUi?.('belot-auction-click', 0.22);
                  setTrump(s.trump);
                }}
                className={cn(
                  actionBtnBase,
                  'min-h-0 shrink-0 rounded-full border-2 p-0 font-bold leading-none',
                  suitBtnRound,
                  active ? suitActiveStyle : 'border-amber-900/40 bg-gradient-to-b from-amber-100/25 to-amber-950/35 text-foreground',
                  !active && 'hover:border-amber-700/50',
                  tieBreakAllowed && !active && 'border-amber-300/55 ring-2 ring-amber-300/45',
                  tieBreakBlocked && !active && 'opacity-45',
                  !active && red && 'text-rose-500',
                  !active && !red && 'text-foreground',
                )}
              >
                {active ? btnShine : null}
                {s.label}
              </button>
            );
          })}
          <button
            type="button"
            title={
              trump === TRUMP_ALL_TRUMP
                ? 'Все козыри (AT), тап — переключить на БК'
                : 'Без козыря (БК), тап — переключить на AT'
            }
            disabled={disabledAll}
            onClick={() => {
              if (trump === TRUMP_NO_TRUMP || trump === TRUMP_ALL_TRUMP) {
                cycleNtTrump();
              } else {
                playUi?.('belot-auction-click', 0.22);
                setTrump(TRUMP_NO_TRUMP);
                setHasManualBidTouch(true);
              }
            }}
            className={cn(
              actionBtnBase,
              'flex min-h-0 shrink-0 flex-col items-center justify-center rounded-full border-2 p-0 leading-none',
              suitBtnRound,
              trump === TRUMP_NO_TRUMP || trump === TRUMP_ALL_TRUMP
                ? suitActiveStyle
                : 'border-amber-900/40 bg-gradient-to-b from-white/90 to-amber-100/30 text-foreground',
            )}
          >
            {(trump === TRUMP_NO_TRUMP || trump === TRUMP_ALL_TRUMP) ? btnShine : null}
            <span className={isInline ? 'text-base font-black' : 'text-lg font-black'}>А</span>
            <span className="mt-0.5 text-[8px] font-bold uppercase leading-none text-muted-foreground">
              {trump === TRUMP_ALL_TRUMP ? 'AT' : 'БК'}
            </span>
          </button>
        </div>
      </section>

      {/* Ряд 3: Заказ | Контра | Пас */}
      <div className={cn('border-t border-border/40', isInline ? 'pt-1' : 'pt-2')}>
        <div className={cn('flex items-stretch gap-1 sm:gap-1.5', row3Min)}>
          <button
            type="button"
            disabled={!canOrder || disabledAll}
            title={
              !candidateBid
                ? isMoldovan
                  ? 'Режим и CP'
                  : 'Масть и CP'
                : canOrder
                  ? isMoldovan
                    ? 'STRIGARE'
                    : orderLabel
                  : isMoldovan
                    ? 'Не перебивает bazar'
                    : 'Слабее лучшей'
            }
            onClick={() => {
              playUi?.('raise', 0.35);
              handleOrder();
            }}
            className={cn(
              actionBtnBase,
              'min-w-0 flex-1 py-1.5 text-[9px] font-black uppercase tracking-wide sm:py-2 sm:text-[10px]',
              canOrder
                ? cn(
                    sandSideStyle,
                    isMoldovan && 'animate-[pulse_1.6s_ease-in-out_infinite] motion-reduce:animate-none',
                  )
                : orderDisabledStyle,
            )}
          >
            {canOrder ? btnShine : null}
            {orderLabel}
          </button>
          <button
            type="button"
            disabled={!showContraBlock || !canContra || auctionMultIsMax || disabledAll}
            title={auctionMultIsMax ? '×4' : canContra ? 'Контра' : 'Нельзя'}
            onClick={() => {
              playUi?.('button-click', 0.25);
              onContra?.();
            }}
            className={cn(
              actionBtnBase,
              'min-w-0 flex-1 py-1.5 text-[9px] font-black uppercase tracking-wide sm:py-2 sm:text-[10px]',
              showContraBlock && canContra && !auctionMultIsMax ? contraPrimaryStyle : orderDisabledStyle,
            )}
          >
            {showContraBlock && canContra && !auctionMultIsMax ? btnShine : null}
            Контра
          </button>
          <button
            type="button"
            disabled={disabledAll}
            onClick={() => {
              playUi?.('fold', 0.35);
              onPass();
            }}
            className={cn(
              actionBtnBase,
              'min-w-0 flex-1 py-1.5 text-[9px] font-black uppercase tracking-wide sm:py-2 sm:text-[10px]',
              sandSideStyle,
            )}
          >
            {btnShine}
            {passLabel}
          </button>
        </div>
        {showContraBlock ? (
          <button
            type="button"
            disabled={!canRecontra || auctionMultIsMax || disabledAll}
            title={auctionMultIsMax ? '×4' : canRecontra ? 'Реконтра' : 'Нельзя'}
            onClick={() => {
              playUi?.('button-click', 0.25);
              onRecontra?.();
            }}
            className={cn(
              actionBtnBase,
              'w-full py-1.5 text-[10px] font-bold uppercase tracking-wide',
              isInline ? 'mt-0.5' : 'mt-1.5',
              canRecontra && !auctionMultIsMax ? recontraStyle : orderDisabledStyle,
            )}
          >
            Реконтра
          </button>
        ) : null}
      </div>
    </div>
  );

  if (isInline) {
    return (
      <div
        className={cn(
          panelShell,
          'relative z-10 mx-auto flex w-full max-w-[min(100%,22rem)] flex-col overflow-hidden rounded-2xl',
        )}
        role="region"
        aria-label={turnHeadline}
      >
        {panelInner}
      </div>
    );
  }

  /** Выше fixed-полёта карт онлайна (z~205), ниже reveal-оверлея (z~235). Иначе fixed «застревает» в stacking context предков. */
  const modalBackdropZ = 'z-[210]';
  const modalPanelZ = 'z-[211]';

  const modalNode = (
    <>
      <div
        className={cn('pointer-events-none fixed inset-0 bg-transparent', modalBackdropZ)}
        aria-hidden
      />
      <div
        className={cn(
          panelShell,
          'pointer-events-auto fixed inset-x-0 bottom-0 mx-auto flex w-full max-w-[min(100%,22rem)] max-h-[min(58vh,520px)] flex-col overflow-hidden rounded-t-2xl border-b-0 sm:bottom-[max(0.35rem,env(safe-area-inset-bottom))] sm:rounded-2xl',
          modalPanelZ,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={turnHeadline}
      >
        {panelInner}
      </div>
    </>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalNode, document.body);
  }
  return modalNode;
}
